/**
 * Das Gebäude Rosenstraße 30 nach den Bestandsplänen BA.01–BA.04 (M 1:100).
 *
 * Die Wände kommen als Daten herein (Bestand: walls.ts, Planung: planung.ts). Hier wird daraus Geometrie erzeugt,
 * plus alles, was keine einfache Wand ist: Decken, Treppe, Schornstein, Dach,
 * Garage, Lichtschächte, Gelände und die Nachbarhälfte.
 */
import * as THREE from "three";
import { FLOORS, RIDGE_Y, RIDGE_Z, ROOF_T, zTop, zUnder, GARDEN_Z, WELL, GB, ST, ROOF_ANGLE, type Rect } from "./constants";
import { M, mat } from "./materials";
import { boxPlan, prismPlan, rect, wallMesh, type Opening, type Pt } from "./geometry";
import type { WallDef } from "./walls";

export interface HouseModel {
  /** ein Group je Geschoss (UG, EG, OG, DG) */
  floors: THREE.Group[];
  roof: THREE.Group;
  neighbour: THREE.Group;
  site: THREE.Group;
  /** Kollisions-Rechtecke je Geschoss, für Rundgang und Möbel */
  colliders: Rect[][];
}

const WALKABLE: Partial<Record<Opening["k"], true>> = { door: true, gdoor: true, edoor: true };

export function buildHouse(walls: WallDef[]): HouseModel {
  const floors = FLOORS.map((f) => { const g = new THREE.Group(); g.name = f.id; return g; });
  const roofGroup = new THREE.Group(); roofGroup.name = "Dach";
  const nbGroup = new THREE.Group(); nbGroup.name = "Nachbar";
  const S = new THREE.Group(); S.name = "Gelände";
  const colliders: Rect[][] = FLOORS.map(() => []);
  const addCol = (fi: number, x0: number, y0: number, x1: number, y1: number) => colliders[fi].push([x0, y0, x1, y1]);

  /* ---------- Wände ---------- */
  function addWall(w: WallDef) {
    const { f: fi, x0, y0, x1, y1, ops = [], ext = null, roof = null, opt = {} } = w;
    const f = FLOORS[fi], G = floors[fi];
    const alongX = x1 - x0 >= y1 - y0;
    const start = alongX ? x0 : y0, L = alongX ? x1 - x0 : y1 - y0;
    const zf = roof === "top" ? zTop : zUnder;
    const topFn = roof ? (u: number) => zf(alongX ? (y0 + y1) / 2 : y0 + u) - f.z : () => opt.h ?? f.h - 0.013;
    const ridgeU = !alongX && roof ? RIDGE_Y - y0 : null;
    const sk = 0.035;
    let core: Rect = [x0, y0, x1, y1], skin: Rect | null = null;
    if (ext === "S") { skin = [x0, y0, x1, y0 + sk]; core = [x0, y0 + sk, x1, y1]; }
    if (ext === "N") { skin = [x0, y1 - sk, x1, y1]; core = [x0, y0, x1, y1 - sk]; }
    if (ext === "W") { skin = [x0, y0, x0 + sk, y1]; core = [x0 + sk, y0, x1, y1]; }
    if (ext === "E") { skin = [x1 - sk, y0, x1, y1]; core = [x0, y0, x1 - sk, y1]; }
    G.add(wallMesh(core[0], core[1], core[2], core[3], alongX, start, L, topFn, ridgeU, ops, mat.wall, f.z));
    const skinTop = roof ? topFn : () => opt.h ?? f.h; // Fassade läuft geschossübergreifend durch
    if (skin) G.add(wallMesh(skin[0], skin[1], skin[2], skin[3], alongX, start, L, skinTop, ridgeU, ops, mat.facade, f.z));
    // Füllungen
    const c = alongX ? (y0 + y1) / 2 : (x0 + x1) / 2, th = alongX ? y1 - y0 : x1 - x0;
    const R = (a0: number, a1: number, cc: number, t: number, za: number, zb: number, m: THREE.Material) =>
      alongX ? boxPlan(a0, a1, cc - t / 2, cc + t / 2, za, zb, m) : boxPlan(cc - t / 2, cc + t / 2, a0, a1, za, zb, m);
    for (const o of ops) {
      const za = f.z + o.b0, zb = f.z + o.b1, fr = 0.055;
      if (o.k === "win") {
        const ft = 0.07;
        G.add(R(o.a0, o.a1, c, ft, zb - fr, zb, mat.frame), R(o.a0, o.a1, c, ft, za, za + fr, mat.frame));
        G.add(R(o.a0, o.a0 + fr, c, ft, za, zb, mat.frame), R(o.a1 - fr, o.a1, c, ft, za, zb, mat.frame));
        if (o.a1 - o.a0 > 1.55) { const m = (o.a0 + o.a1) / 2; G.add(R(m - fr / 2, m + fr / 2, c, ft, za, zb, mat.frame)); }
        const gl = R(o.a0, o.a1, c, 0.012, za, zb, mat.glass); gl.castShadow = false; G.add(gl);
      } else if (o.k === "door") {
        const jt = th + 0.02;
        G.add(R(o.a0, o.a0 + 0.04, c, jt, za, zb, mat.jamb), R(o.a1 - 0.04, o.a1, c, jt, za, zb, mat.jamb), R(o.a0, o.a1, c, jt, zb - 0.04, zb, mat.jamb));
      } else if (o.k === "gdoor") {
        G.add(R(o.a0, o.a0 + fr, c, 0.07, za, zb, mat.frame), R(o.a1 - fr, o.a1, c, 0.07, za, zb, mat.frame), R(o.a0, o.a1, c, 0.07, zb - fr, zb, mat.frame));
      } else if (o.k === "edoor") {
        G.add(R(o.a0, o.a0 + 0.05, c, 0.08, za, zb, mat.door), R(o.a1 - 0.05, o.a1, c, 0.08, za, zb, mat.door), R(o.a0, o.a1, c, 0.08, zb - 0.05, zb, mat.door));
        const wl = o.a1 - o.a0 - 0.1; // offener Flügel, nach innen
        G.add(boxPlan(o.a0 + 0.05, o.a0 + 0.1, y1, y1 + wl, za, zb - 0.06, mat.door));
      } else if (o.k === "block") {
        G.add(R(o.a0, o.a1, c, 0.1, za, zb, mat.gblock));
      } else if (o.k === "gate") {
        G.add(R(o.a0, o.a1, c, 0.05, za, zb, mat.gate));
        for (let k = 1; k < 6; k++) { const z = za + ((zb - za) * k) / 6; G.add(R(o.a0 + 0.05, o.a1 - 0.05, c - 0.03, 0.01, z - 0.01, z + 0.01, mat.woodDark)); }
      }
    }
    // Kollision: Wand ohne begehbare Öffnungen
    if (opt.collide !== false) {
      const open = ops.filter((o) => WALKABLE[o.k]).map((o) => [o.a0, o.a1] as Pt).sort((a, b) => a[0] - b[0]);
      let cur = start; const end = start + L;
      const push = (a: number, b: number) => { if (b - a < 0.01) return; if (alongX) addCol(fi, a, y0, b, y1); else addCol(fi, x0, a, x1, b); };
      for (const [a, b] of open) { push(cur, a); cur = Math.max(cur, b); }
      push(cur, end);
    }
  }
  walls.forEach(addWall);

  /* ---------- Geschossdecken ---------- */
  const stepped: Pt[] = [[0.04, 0.04], [7.71, 0.04], [7.71, 10.96], [4.24, 10.96], [4.24, 10.66], [0.04, 10.66]];
  const full = rect(0.04, 0.04, 7.71, 10.96);
  const wellHole = rect(WELL.x0, WELL.y0, WELL.x1, WELL.y1);
  const floorMats = [mat.fUG, mat.fEG, mat.fOG, mat.fDG];
  FLOORS.forEach((f, i) => {
    const shape = i === 1 || i === 2 ? stepped : full;
    const holes = i > 0 ? [wellHole] : [];
    if (i > 0) floors[i].add(prismPlan(shape, holes, f.z - 0.2, f.z - 0.012, mat.slab));
    else floors[i].add(prismPlan(shape, holes, -0.25, -0.012, mat.base));
    floors[i].add(prismPlan(shape, holes, f.z - 0.012, f.z, floorMats[i]));
  });

  /* ---------- UG: Garage mit Außentreppe, Lichtschacht ---------- */
  const U = floors[0];
  U.add(boxPlan(-3.38, 0, 1.87, GB - 0.25, -0.2, 0, mat.base));
  U.add(boxPlan(-3.38, 0, 0.79, 1.63, -0.2, 0, mat.paving));
  for (let i = 0; i < 14; i++) { const ya = GB + i * 0.307; U.add(boxPlan(-3.38, -2.02, ya, ya + 0.307, -0.2, ((i + 1) * GARDEN_Z) / 14, mat.paving)); }
  addCol(0, -3.38, GB, -2.02, ST); addCol(1, -3.38, GB, -2.02, ST);

  // Gitterrost (Rahmen + Tragstäbe quer zur langen Seite)
  function grateTop(G: THREE.Group, x0: number, x1: number, y0: number, y1: number, z1: number) {
    const z0 = z1 - 0.04, f = 0.03;
    G.add(boxPlan(x0, x1, y0, y0 + f, z0, z1, mat.grate), boxPlan(x0, x1, y1 - f, y1, z0, z1, mat.grate),
      boxPlan(x0, x0 + f, y0, y1, z0, z1, mat.grate), boxPlan(x1 - f, x1, y0, y1, z0, z1, mat.grate));
    if (x1 - x0 > y1 - y0) {
      for (let x = x0 + 0.033; x < x1 - f; x += 0.033) G.add(boxPlan(x - 0.003, x + 0.003, y0 + f, y1 - f, z0, z1, mat.grate, false));
      for (let y = y0 + 0.1; y < y1 - 0.05; y += 0.1) G.add(boxPlan(x0 + f, x1 - f, y - 0.004, y + 0.004, z0, z1 - 0.01, mat.grate, false));
    } else {
      for (let y = y0 + 0.033; y < y1 - f; y += 0.033) G.add(boxPlan(x0 + f, x1 - f, y - 0.003, y + 0.003, z0, z1, mat.grate, false));
      for (let x = x0 + 0.1; x < x1 - 0.05; x += 0.1) G.add(boxPlan(x - 0.004, x + 0.004, y0 + f, y1 - f, z0, z1 - 0.01, mat.grate, false));
    }
  }
  // Lichtschacht: Fenster oben in der Wand, in der Garage schräg zugemauert, Bodenfenster in der Terrasse
  const SHAFT = { x0: -0.4, y0: 5.4, y1: 6.3 };
  {
    const wedge = (pts: Pt[], ya: number, yb: number) => {
      const sh = new THREE.Shape(pts.map((p) => new THREE.Vector2(p[0], p[1])));
      const o = new THREE.Mesh(new THREE.ExtrudeGeometry(sh, { depth: yb - ya, bevelEnabled: false }), mat.wall);
      o.position.z = -yb; o.castShadow = o.receiveShadow = true; U.add(o);
    };
    wedge([[0, 1.5], [0, 1.64], [SHAFT.x0 + 0.12, 2.4], [SHAFT.x0, 2.4]], SHAFT.y0 - 0.1, SHAFT.y1 + 0.1);
    const cheek: Pt[] = [[0, 1.5], [0, 2.4], [SHAFT.x0, 2.4]];
    wedge(cheek, SHAFT.y0 - 0.1, SHAFT.y0); wedge(cheek, SHAFT.y1, SHAFT.y1 + 0.1);
    addCol(0, SHAFT.x0, SHAFT.y0 - 0.1, 0, SHAFT.y1 + 0.1);
  }
  U.add(boxPlan(4.5, 7.75, -1.0, 0, 2.4, 2.48, mat.metal)); // Vordach
  U.add(boxPlan(4.8, 6.5, -0.9, 0, -0.1, 0.12, mat.paving)); // Stufe

  /* ---------- EG: Dachterrasse auf der Garage ---------- */
  const E = floors[1];
  E.add(boxPlan(-3.59, 0, 0.79, SHAFT.y0, 2.4, 2.606, mat.paving));
  E.add(boxPlan(-3.59, 0, SHAFT.y1, GB, 2.4, 2.606, mat.paving));
  E.add(boxPlan(-3.59, SHAFT.x0, SHAFT.y0, SHAFT.y1, 2.4, 2.606, mat.paving));
  grateTop(E, SHAFT.x0, 0, SHAFT.y0, SHAFT.y1, 2.606);
  const rail = (fi: number, x0: number, y0: number, x1: number, y1: number, z: number, m: THREE.Material = mat.woodDark, h = 1.0) => {
    const g = floors[fi];
    g.add(boxPlan(x0, x1, y0, y1, z + h - 0.06, z + h, m));
    const alongX = x1 - x0 > y1 - y0, L = alongX ? x1 - x0 : y1 - y0, n = Math.max(2, Math.round(L / 0.12));
    for (let k = 0; k <= n; k++) {
      const a = (alongX ? x0 : y0) + (L * k) / n;
      if (alongX) g.add(boxPlan(a - 0.02, a + 0.02, y0, y1, z, z + h, m)); else g.add(boxPlan(x0, x1, a - 0.02, a + 0.02, z, z + h, m));
    }
    addCol(fi, x0, y0, x1, y1);
  };
  rail(1, -3.59, 0.79, 0, 0.85, 2.6); rail(1, -3.59, 0.85, -3.53, GB, 2.6);
  rail(1, -3.59, GB - 0.06, -1.92, GB, 2.6); // Stirnseite über der Garagentreppe
  rail(1, -1.96, GB, -1.92, ST, GARDEN_Z, mat.metal);

  /* ---------- OG: Balkon ---------- */
  const O = floors[2];
  O.add(boxPlan(0, 4.19, 10.7, 12.38, 5.15, 5.35, mat.paving));
  const slats = (G: THREE.Group, x0: number, y0: number, x1: number, y1: number, z: number) => {
    for (let k = 0; k < 5; k++) { const za = z + 0.18 + k * 0.17; G.add(boxPlan(x0, x1, y0, y1, za, za + 0.11, mat.wood)); }
  };
  slats(O, 0, 12.3, 4.19, 12.36, 5.35); slats(O, 0.02, 10.7, 0.08, 12.3, 5.35); slats(O, 4.11, 10.7, 4.17, 12.3, 5.35);
  ([[0.05, 12.33], [2.1, 12.33], [4.14, 12.33]] as Pt[]).forEach(([x, y]) => O.add(boxPlan(x - 0.04, x + 0.04, y - 0.04, y + 0.04, 5.35, 6.35, mat.woodDark)));
  addCol(2, 0, 12.28, 4.19, 12.4); addCol(2, -0.1, 10.7, 0.1, 12.4); addCol(2, 4.09, 10.7, 4.3, 12.4);

  /* ---------- DG ---------- */
  const D = floors[3];
  D.add(boxPlan(6.1, 6.14, 2.07, 3.08, 8.1, 9.1, mat.metal)); addCol(3, 6.08, 2.07, 6.16, 3.08);
  // Dach-Kollision: nicht unter 1,40 m lichte Höhe laufen
  addCol(3, 0, 0, 7.75, 1.15); addCol(3, 0, 9.85, 7.75, 11);

  // Fassadenecken (verdecken Wandstirnseiten)
  const cornerSkin = (fi: number, xa: number, xb: number, ya: number, yb: number) =>
    floors[fi].add(boxPlan(xa, xb, ya, yb, FLOORS[fi].z, FLOORS[fi].z + FLOORS[fi].h - 0.001, mat.facade));
  [0, 1, 2].forEach((fi) => cornerSkin(fi, -0.002, 0.035, -0.002, 0.3));
  cornerSkin(0, -0.002, 0.035, 10.4, 11.002);
  [1, 2].forEach((fi) => { cornerSkin(fi, -0.002, 0.035, 10.4, 10.702); cornerSkin(fi, 4.163, 4.201, 10.7, 11.002); });

  /* ---------- Schornstein ---------- */
  // liegt in der Mauer und steht nach links (Richtung Garage) etwas aus der Wand vor, weiß verputzt
  const CH_OUT = 0.2;
  // [Geschoss, Wand links, Anfang, Wand rechts, Ende in der Wand, Ende Vorsprung (bündig mit Querwand)]
  ([[0, 4.26, 5.4, 4.5, 6.78, 6.76], [1, 4.24, 5.4, 4.48, 6.78, 6.76], [2, 4.28, 5.41, 4.51, 6.73, 6.64], [3, 4.26, 5.4, 4.5, 6.78, 6.64]] as number[][])
    .forEach(([fi, a, b, c, d, e]) => {
      const z0 = FLOORS[fi].z, z1 = fi === 3 ? 10.55 : z0 + FLOORS[fi].h - 0.013;
      floors[fi].add(boxPlan(a, c, b, d, z0, z1, mat.wall), boxPlan(a - CH_OUT, a, b, e, z0, z1, mat.wall));
      addCol(fi, a, b, c, d); addCol(fi, a - CH_OUT, b, a, e);
    });
  // im Dachgeschoss weiß bis unter die Dachhaut (in Streifen, weil das Dach schräg ist), darüber wie die Dacheindeckung
  for (let k = 0; k < 6; k++) {
    const ya = 5.4 + ((6.64 - 5.4) * k) / 6, yb = 5.4 + ((6.64 - 5.4) * (k + 1)) / 6, zu = Math.min(zUnder(ya), zUnder(yb));
    D.add(boxPlan(4.26 - CH_OUT, 4.5, ya, yb, 10.55, zu, mat.wall), boxPlan(4.26 - CH_OUT, 4.5, ya, yb, zu, 11.86, mat.roof));
  }
  D.add(boxPlan(4.2 - CH_OUT, 4.56, 5.34, 6.7, 11.86, 11.92, mat.roof));
  { const p = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.6, 16), mat.steel); p.position.set(4.38 - CH_OUT / 2, 12.2, -6.02); p.castShadow = true; D.add(p); }

  /* ---------- Treppe (viertelgewendelt, zweimal) ---------- */
  const INNER: Pt[] = [[6.13, 3.08], [6.43, 3.08], [6.43, 4.52], [6.13, 4.52]];
  const OUTER: Pt[] = [[6.13, 2.07], [7.45, 2.07], [7.45, 5.52], [6.13, 5.52]];
  const TK = [0, 0.3, 0.7, 1];
  const onPath = (P: Pt[], t: number): Pt => {
    for (let k = 0; k < 3; k++) if (t <= TK[k + 1] + 1e-9) { const u = (t - TK[k]) / (TK[k + 1] - TK[k]); return [P[k][0] + (P[k + 1][0] - P[k][0]) * u, P[k][1] + (P[k + 1][1] - P[k][1]) * u]; }
    return P[3];
  };
  function buildStair(fi: number, rise: number, n: number) {
    const z0 = FLOORS[fi].z;
    for (let i = 0; i < n; i++) {
      const t0 = i / n, t1 = (i + 1) / n;
      const poly: Pt[] = [onPath(INNER, t0)];
      for (let k = 1; k < 3; k++) if (TK[k] > t0 + 1e-6 && TK[k] < t1 - 1e-6) poly.push(INNER[k]);
      poly.push(onPath(INNER, t1), onPath(OUTER, t1));
      for (let k = 2; k >= 1; k--) if (TK[k] > t0 + 1e-6 && TK[k] < t1 - 1e-6) poly.push(OUTER[k]);
      poly.push(onPath(OUTER, t0));
      const top = z0 + ((i + 1) * rise) / n;
      floors[fi].add(prismPlan(poly, null, Math.max(z0, top - 0.24), top, mat.stair));
    }
    floors[fi].add(boxPlan(6.13, 6.43, 3.08, 4.52, z0, z0 + FLOORS[fi].h - 0.013, mat.wall));
    addCol(fi, WELL.x0, WELL.y0, WELL.x1, WELL.y1);
  }
  buildStair(0, 2.6, 14); buildStair(1, 2.75, 15); buildStair(2, 2.75, 15);
  D.add(boxPlan(6.13, 6.43, 3.08, 4.52, 8.1, 9.1, mat.wall)); addCol(3, WELL.x0, WELL.y0, WELL.x1, WELL.y1);

  /* ---------- Dach ---------- */
  type Hole = [number, number, number, number]; // u0, u1, v0, v1
  const cosA = Math.cos(ROOF_ANGLE), sinA = Math.sin(ROOF_ANGLE);
  const vOf = (y: number) => (y - RIDGE_Y) / cosA;
  function roofSlope(side: "back" | "front", uMin: number, uMax: number, holes: Hole[], m: THREE.Material, G: THREE.Group) {
    const vMax = (11.6 - RIDGE_Y) / cosA;
    const s = new THREE.Shape([new THREE.Vector2(uMin, 0), new THREE.Vector2(uMax, 0), new THREE.Vector2(uMax, vMax), new THREE.Vector2(uMin, vMax)]);
    for (const h of holes) { const p = new THREE.Path(); p.moveTo(h[0], h[2]); p.lineTo(h[1], h[2]); p.lineTo(h[1], h[3]); p.lineTo(h[0], h[3]); p.closePath(); s.holes.push(p); }
    const o = new THREE.Mesh(new THREE.ExtrudeGeometry(s, { depth: ROOF_T, bevelEnabled: false }), m);
    const X = side === "back" ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(-1, 0, 0);
    const Y = new THREE.Vector3(0, -sinA, side === "back" ? -cosA : cosA);
    const Z = new THREE.Vector3().crossVectors(X, Y);
    const P = new THREE.Vector3(0, RIDGE_Z, -RIDGE_Y).addScaledVector(Z, -ROOF_T);
    o.matrixAutoUpdate = false; o.matrix.makeBasis(X, Y, Z).setPosition(P);
    o.castShadow = o.receiveShadow = true;
    for (const h of holes) { // Dachfenster
      const gl = new THREE.Mesh(new THREE.BoxGeometry(h[1] - h[0], h[3] - h[2], 0.02), mat.glass); gl.position.set((h[0] + h[1]) / 2, (h[2] + h[3]) / 2, ROOF_T - 0.02); o.add(gl);
      const cu = (h[0] + h[1]) / 2, cv = (h[2] + h[3]) / 2, w = h[1] - h[0], l = h[3] - h[2], b = 0.07;
      ([[cu, h[2] - b / 2, w + 2 * b, b], [cu, h[3] + b / 2, w + 2 * b, b], [h[0] - b / 2, cv, b, l], [h[1] + b / 2, cv, b, l]] as number[][]).forEach(([x, y, bw, bl]) => {
        const fr = new THREE.Mesh(new THREE.BoxGeometry(bw, bl, 0.08), mat.black); fr.position.set(x, y, ROOF_T + 0.02); o.add(fr);
      });
    }
    G.add(o); return o;
  }
  const skylights: Hole[] = [[1.09, 2.35, vOf(7.58), vOf(8.66)], [5.58, 6.47, vOf(7.88), vOf(8.66)]];
  roofSlope("back", -0.15, 7.75, skylights, mat.roof, roofGroup);
  roofSlope("front", -7.75, 0.15, [], mat.roof, roofGroup);
  // Innenverkleidung unter den Sparren (hell), damit das DG innen nicht dunkel wirkt
  function lining(side: "back" | "front", holes: Hole[]) {
    const u0 = side === "back" ? 0.3 : -7.45, u1 = side === "back" ? 7.45 : -0.3;
    const n = new THREE.Vector3(0, cosA, side === "back" ? -sinA : sinA);
    const vMax = vOf(10.7);
    const sh = new THREE.Shape([new THREE.Vector2(u0, 0), new THREE.Vector2(u1, 0), new THREE.Vector2(u1, vMax), new THREE.Vector2(u0, vMax)]);
    for (const h of holes) { const p = new THREE.Path(); p.moveTo(h[0], h[2]); p.lineTo(h[1], h[2]); p.lineTo(h[1], h[3]); p.lineTo(h[0], h[3]); p.closePath(); sh.holes.push(p); }
    const o = new THREE.Mesh(new THREE.ExtrudeGeometry(sh, { depth: 0.012, bevelEnabled: false }), mat.wall);
    const X = side === "back" ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(-1, 0, 0);
    const Y = new THREE.Vector3(0, -sinA, side === "back" ? -cosA : cosA);
    const Z = new THREE.Vector3().crossVectors(X, Y);
    o.matrixAutoUpdate = false; o.matrix.makeBasis(X, Y, Z).setPosition(new THREE.Vector3(0, RIDGE_Z, -RIDGE_Y).addScaledVector(n, -ROOF_T - 0.014));
    o.receiveShadow = true; roofGroup.add(o);
  }
  lining("back", skylights); lining("front", []);
  roofGroup.add(boxPlan(-0.15, 7.75, 5.4, 5.6, 11.46, 11.58, mat.roof)); // Firstbalken
  roofGroup.add(boxPlan(-0.15, 7.75, -0.72, -0.58, 7.9, 8.02, mat.metal), boxPlan(-0.15, 7.75, 11.58, 11.72, 7.9, 8.02, mat.metal));
  // Schneefanggitter wie auf den Fotos
  const snowRail = (y: number) => { const z = zTop(y) + 0.12; roofGroup.add(boxPlan(-0.1, 7.7, y - 0.02, y + 0.02, z, z + 0.03, mat.black, false)); };
  snowRail(0.6); snowRail(10.4);

  /* ---------- Nachbarhälfte Nr. 32 ---------- */
  nbGroup.add(boxPlan(7.75, 15.5, 0, 11, 0, 8.3, mat.facadeN));
  roofSlope("back", 7.75, 15.65, [], mat.roofN, nbGroup);
  roofSlope("front", -15.65, -7.75, [], mat.roofN, nbGroup);
  nbGroup.add(wallMesh(15.2, 0, 15.5, 11, false, 0, 11, (u) => zTop(u) - 8.36, RIDGE_Y, [], mat.facadeN, 8.36));
  ([[1.22, 3.11, 0.86, 2.06], [5.1, 7.3, 0, 2.1]] as number[][]).forEach(([a, b, s, h]) => nbGroup.add(boxPlan(15.5 - b, 15.5 - a, -0.02, 0, s, h, mat.black, false)));
  [2.6, 5.35].forEach((z) => ([[1.0, 2.0], [3.5, 4.5], [5.6, 6.6]] as Pt[]).forEach(([a, b]) => nbGroup.add(boxPlan(7.75 + a + 0.3, 7.75 + b + 0.3, -0.02, 0, z + 0.84, z + 2.08, mat.black, false))));
  for (const fi of [0, 1]) addCol(fi, 7.75, 0, 20, 11);

  /* ---------- Gelände ---------- */
  S.add(boxPlan(-14, 22, -16, -3.4, -0.4, -0.14, mat.asphalt));
  S.add(boxPlan(-14, 22, -3.4, -3.1, -0.4, -0.02, mat.curb));
  S.add(boxPlan(-3.59, 0, -3.1, 0.79, -0.4, -0.03, mat.paving));
  S.add(boxPlan(0, 4.5, -3.1, 0, -0.4, -0.04, mat.grass));
  S.add(boxPlan(4.5, 7.75, -3.1, 0, -0.4, -0.03, mat.paving));
  S.add(boxPlan(7.75, 22, -3.1, 0, -0.4, -0.04, mat.grass));
  S.add(boxPlan(-14, -3.59, -3.1, GB, -0.4, -0.03, mat.paving));
  const earthMat = [mat.soil, mat.soil, mat.grass, mat.soil, mat.soil, mat.soil];
  const earth = (x0: number, x1: number, y0: number, y1: number, top = GARDEN_Z) => S.add(boxPlan(x0, x1, y0, y1, -0.4, top, earthMat));
  earth(-14, 22, ST, 32);
  const LW = { x0: 5.14, x1: 7.14, y0: 11.0, y1: 11.4 }; // Lichtschacht Kellerraum 17,08 m²
  earth(0, 4.2, 10.7, ST);
  earth(4.2, 7.75, LW.y1, ST); earth(4.2, LW.x0, LW.y0, LW.y1); earth(LW.x1, 7.75, LW.y0, LW.y1); earth(LW.x0, LW.x1, LW.y0, LW.y1, 1.4);
  earth(-1.78, 0, GB, ST); earth(-14, -3.59, GB, ST); earth(7.75, 22, 11.0, ST);
  {
    const sh = new THREE.Shape(([[LW.y0, 1.5], [LW.y0, 1.64], [LW.y1 - 0.12, GARDEN_Z], [LW.y1, GARDEN_Z], [LW.y1, 1.4], [LW.y0, 1.4]] as Pt[]).map((p) => new THREE.Vector2(p[0], p[1])));
    const o = new THREE.Mesh(new THREE.ExtrudeGeometry(sh, { depth: LW.x1 - LW.x0, bevelEnabled: false }), mat.wall);
    o.rotation.y = Math.PI / 2; o.position.x = LW.x0; o.castShadow = o.receiveShadow = true; S.add(o); // schräg gemauert
  }
  const TERR_Y = ST + 2.0; // Hinterkante Gartenterrasse
  S.add(boxPlan(0, 7.75, 10.7, LW.y0, GARDEN_Z, GARDEN_Z + 0.02, mat.paving));
  S.add(boxPlan(0, LW.x0, LW.y0, LW.y1, GARDEN_Z, GARDEN_Z + 0.02, mat.paving)); S.add(boxPlan(LW.x1, 7.75, LW.y0, LW.y1, GARDEN_Z, GARDEN_Z + 0.02, mat.paving));
  S.add(boxPlan(0, 7.75, LW.y1, TERR_Y, GARDEN_Z, GARDEN_Z + 0.02, mat.paving));
  grateTop(S, LW.x0, LW.x1, LW.y0, LW.y1, GARDEN_Z + 0.02);
  S.add(boxPlan(-1.78, 0, GB, TERR_Y, GARDEN_Z, GARDEN_Z + 0.02, mat.paving)); // Verbindung Garagenterrasse ↔ Gartenterrasse
  S.add(boxPlan(-3.59, -1.78, ST, TERR_Y, GARDEN_Z, GARDEN_Z + 0.02, mat.paving)); // vor dem oberen Treppenaustritt
  S.add(boxPlan(-3.59, 7.75, TERR_Y, TERR_Y + 0.25, GARDEN_Z, GARDEN_Z + 0.25, mat.curb));
  // Pergola über dem Fenster links (vom Garten gesehen)
  ([[4.45, 13.55], [7.62, 13.55]] as Pt[]).forEach(([x, y]) => S.add(boxPlan(x - 0.07, x + 0.07, y - 0.07, y + 0.07, GARDEN_Z, 4.95, mat.woodDark)));
  S.add(boxPlan(4.3, 7.75, 13.48, 13.62, 4.8, 4.95, mat.woodDark));
  { const g = new THREE.Mesh(new THREE.PlaneGeometry(3.45, 2.65), mat.pergolaGlass); g.rotation.x = -Math.PI / 2 + 0.06; g.position.set(6.02, 4.98, -12.3); S.add(g); }
  // Apfelbaum im Garten
  {
    const t = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.16, 2.2, 10), mat.wood); t.position.set(9.0, GARDEN_Z + 1.1, -15.8); t.castShadow = true; S.add(t);
    const c = new THREE.Mesh(new THREE.IcosahedronGeometry(1.9, 1), M(0x5f8a43, { r: 1 })); c.position.set(9.0, GARDEN_Z + 3.4, -15.8); c.castShadow = true; S.add(c);
  }
  // Weltgrenzen
  for (const fi of [0, 1]) { addCol(fi, -14, -17, 22, -16); addCol(fi, -14, 32, 22, 33); addCol(fi, -15, -17, -14, 33); addCol(fi, 22, -17, 23, 33); }
  addCol(0, -14, GB, -3.59, 40); addCol(0, -1.78, GB, 0, 40); addCol(0, 0, 11.0, 22, 40); addCol(0, -3.59, ST, -1.78, 40);
  addCol(1, -14, -17, 22, 0); addCol(1, -14, 0, -3.59, GB);

  return { floors, roof: roofGroup, neighbour: nbGroup, site: S, colliders };
}
